import type { JSX } from "react";
import { useState, useEffect } from "react";
import { ulid } from "ulid";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import type { TaxonomyKind, TaxonomyApiItem } from "@recipevault/shared";
import { createTaxonomy, patchTaxonomy } from "./api.js";
import { friendlyError } from "../../shared/http.js";
import { db } from "../../sync/db.js";
import { KIND_LABEL_SINGULAR } from "./kindLabels.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/ui/form.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog.js";
import { Alert, AlertDescription } from "@/ui/alert.js";

const schema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(64, "Name must be ≤ 64 chars")
    .refine((s) => s.trim().length > 0, "Name must not be whitespace"),
  // Always string | null after parse — defaultValues seeds it to null. Dropping
  // .optional() here avoids an input/output type drift under exactOptionalPropertyTypes.
  color: z
    .string()
    .max(32)
    .nullable()
    .transform((v) => (v === "" ? null : v)),
});
type Values = z.infer<typeof schema>;

interface Props {
  kind: TaxonomyKind;
  mode: "create" | "edit";
  open: boolean;
  onClose: () => void;
  onSaved: (item: TaxonomyApiItem) => void;
  initial?: TaxonomyApiItem;
}

export function TaxonomyFormDialog({ kind, mode, open, onClose, onSaved, initial }: Props): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: initial?.name ?? "",
      color: initial?.color ?? null,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name: initial?.name ?? "",
        color: initial?.color ?? null,
      });
      setErr(null);
    }
  }, [open, initial, form]);

  async function submit(values: Values): Promise<void> {
    setSubmitting(true);
    setErr(null);
    try {
      if (mode === "create") {
        const body: { kind: TaxonomyKind; name: string; color?: string } = {
          kind,
          name: values.name.trim(),
        };
        if (kind === "tag" && values.color) body.color = values.color;
        const r = await createTaxonomy(body, ulid());
        // Write-through Dexie so the live query reflects the new row immediately.
        await db.taxonomy.put(r.item);
        onSaved(r.item);
        onClose();
      } else {
        if (!initial) throw new Error("edit mode requires initial item");
        const patch: { name?: string; color?: string | null } = {};
        if (values.name.trim() !== initial.name) patch.name = values.name.trim();
        if (kind === "tag" && (values.color ?? null) !== (initial.color ?? null)) {
          patch.color = values.color ?? null;
        }
        if (Object.keys(patch).length === 0) {
          onClose();
          return;
        }
        const r = await patchTaxonomy(kind, initial.id, patch);
        // Write-through Dexie so the live query reflects the patched row immediately.
        await db.taxonomy.put(r.item);
        onSaved(r.item);
        onClose();
      }
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  }

  const singular = KIND_LABEL_SINGULAR[kind];
  const title = mode === "create" ? `New ${singular}` : `Edit ${singular}`;
  const submitLabel = mode === "create" ? "Create" : "Save";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? `Add a new ${singular} to your household.`
              : `Rename or restyle this ${singular}.`}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              void form.handleSubmit(submit)(e);
            }}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      autoComplete="off"
                      placeholder={`e.g. ${kind === "tag" ? "weeknight" : kind === "category" ? "dinner" : kind === "cuisine" ? "italian" : "vegetarian"}`}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {kind === "tag" && (
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="#10b981 (optional)"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormDescription>
                      Hex like <code>#10b981</code> or a Tailwind class. Leave blank for the default accent.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {err && (
              <Alert variant="destructive">
                <AlertDescription>{err}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={!form.formState.isValid || submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
